import {
  Breadcrumbs,
  Button,
  Inline,
  Skeleton,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import { EmployeeActionsCell, EmployeesList } from '@omnipresentgroup/features';
import {
  type CombinedEmployee,
  CombinedEmployeeType,
  OnboardingStatusValues,
  SOWStatusEnum,
} from '@omnipresentgroup/types';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import { environment } from '../../../../environment';
import {
  ADD_HIRE_PATH,
  EMPLOYEE_DETAILS_PATH,
  getSeatPath,
} from '../../../constants';
import { useManagerUser } from '../../../hooks';

export const EMPLOYEE_LIST_PAGE_TITLE = 'Employees';

export const EmployeesListPage = () => {
  const navigate = useNavigate();

  const { companyId } = useManagerUser();

  const columnHelper = createColumnHelper<CombinedEmployee>();

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'actions',
        cell: (info) => (
          <Inline justify="flex-end">
            {info.row.original.onboardingStatus ===
              SOWStatusEnum.SOW_ACCEPTED && (
              <Button
                size="small"
                variant="secondary"
                onClick={() => {
                  window.location.href = `${environment.apps.OMNIPLATFORM}/companies/${companyId}/pending-employees/${info.row.original.id}/complete-profile`;
                }}
              >
                Complete profile
              </Button>
            )}
            <EmployeeActionsCell
              employeeId={info.row.original.id}
              handleRequestTermination={() => {
                window.location.href = `${environment.apps.OMNIPLATFORM}/companies/${companyId}/employee/${info.row.original.id}/termination-requests/add`;
              }}
              permissions={{
                canRequestTermination:
                  info.row.original.onboardingStatus ===
                  OnboardingStatusValues.ACTIVE,
              }}
            />
          </Inline>
        ),
        header: () => '',
        size: 150,
        meta: {
          onClickStopPropagation: true,
          loader: (
            <Inline gap="8" justify="flex-end">
              <Skeleton height={16} width={24} />
            </Inline>
          ),
        },
      }),
    ],
    [columnHelper],
  );

  return (
    <>
      <Stack gap="16">
        <Breadcrumbs
          crumbs={[
            { id: 'home', title: 'Home', onClick: () => navigate('/') },
            {
              id: 'employees',
              title: EMPLOYEE_LIST_PAGE_TITLE,
              active: true,
            },
          ]}
        />
        <Inline minH={40} align="center" justify="space-between">
          <Typography as="h3" hideParagraphSpacing size="24" weight="medium">
            {EMPLOYEE_LIST_PAGE_TITLE}
          </Typography>
          <Button
            id="add-hire"
            onClick={() => {
              window.location.href = `${
                environment.apps.OMNIPLATFORM
              }${ADD_HIRE_PATH(companyId)}?type=employee`;
            }}
          >
            Add hire
          </Button>
        </Inline>
      </Stack>

      <EmployeesList
        companyId={companyId}
        columnsOverride={columns}
        columnVisibility={{
          id: true,
          country: true,
          annualBaseSalary: true,
          startDate: true,
          onboardingStatus: true,
          type: false,
        }}
        filters={{
          country: true,
          id: true,
          type: true,
          onboardingStatus: true,
        }}
        onRowClick={(row) => {
          window.location.href =
            row.type === CombinedEmployeeType.EMPLOYEE
              ? `${environment.apps.OMNIPLATFORM}${EMPLOYEE_DETAILS_PATH(row.id)}`
              : `${environment.apps.OMNIPLATFORM}${getSeatPath(companyId, row.id)}`;
        }}
        emptyStateProps={{
          id: 'employees-list-empty-state',
          title: 'No employee has been added',
          description: 'Click Add hire button to add your first employee',
        }}
      />
    </>
  );
};
